import React, { Component } from "react";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import { show, hide } from "redux-modal";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import {
  Button,
  Frame,
  FrameContent,
  Search,
  DisplayMedium,
  Thirds,
  Center,
  Left,
  Right,
  spacing,
} from "@conserveit/blocks";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { MsgAlert } from "../../components";
import * as usersActions from "../../redux/modules/users/actionCreators";
import { loadGroups } from "../../redux/modules/lookups/actionCreators";
import { UserTable } from "../../components/UserTable";
import { Wrap } from "../../components/Layout/Page";
import CreateUserModal, {
  CREATE_USER_MODAL,
} from "./components/CreateUserModal";
import EditUserModal, { EDIT_USER_MODAL } from "./components/EditUserModal";

const StyledWrap = styled(Wrap)`
  padding-top: ${spacing.loose};
`;

const StyledTitle = styled(DisplayMedium)`
  text-align: center;
  margin-bottom: ${spacing.loose};
`;

const StyledThirds = styled(Thirds)`
  margin-bottom: ${spacing.default};
`;

function getRolesSelectOptions(user, roles) {
  let roleOptions = roles;
  if (user?.access?.includes("limitRoleOnRegister")) {
    const roleOptionsRestricted = {};
    user.rolesRestriction.forEach((role) => {
      roleOptionsRestricted[role] = role;
    });
    roleOptions = roleOptionsRestricted;
  }
  return Object.keys(roleOptions).map((key) => ({ value: key, label: key }));
}

function isParentUser(user) {
  return user.status === "active" && Boolean(user.is_sub_user) === false;
}

function makeSearchable(users) {
  return users.map((user) => {
    const targetValueString = Object.values(user).join(" ").replace(/,/g, " ");
    return {
      ...user,
      targetValueString,
    };
  });
}

const SEARCH_KEY = "userSearch";

@connect(
  (state) => ({
    users: state.users.users,
    groups: state.lookups.groups,
    roles: state.lookups.roles,
    user: state.auth.user,
    statuses: state.users.statuses,
    adding: state.users.adding,
    editing: state.users.editing,
    viewing: state.users.viewing,
    working: state.users.working,
    toggleDeactivateWorking: state.users.toggleDeactivateWorking,
    modal_data: state.users.modal_data,
    user_form_data: state.users.user_form_data,
    submit_func: state.users.submit_func,
    error: state.users.error,
    success: state.users.success,
    activeModal: state.betaModal,
  }),
  (dispatch) => ({
    ...bindActionCreators(
      {
        ...usersActions,
        loadGroups,
      },
      dispatch
    ),
    closeModal: (modalName) => {
      dispatch(hide(modalName));
    },
    clearForm: () => {
      dispatch(usersActions.handleClose());
    },
    handleCreateUserFormOpen: () => {
      dispatch(usersActions.startAddUser());
      dispatch(show(CREATE_USER_MODAL));
    },
    handleEditUserFormOpen: (user) => {
      dispatch(usersActions.startViewUser(user));
      dispatch(show(EDIT_USER_MODAL));
    },
  })
)
class UserAdmin extends Component {
  static propTypes = {
    adding: PropTypes.bool,
    viewing: PropTypes.bool,
    editing: PropTypes.bool,
    working: PropTypes.bool,
    toggleDeactivateWorking: PropTypes.bool,

    user: PropTypes.object,
    users: PropTypes.array.isRequired,
    groups: PropTypes.array,
    statuses: PropTypes.object,
    roles: PropTypes.object,
    error: PropTypes.string,
    success: PropTypes.string,
    modal_data: PropTypes.object,
    user_form_data: PropTypes.object,
    activeModal: PropTypes.object,
    loadUsers: PropTypes.func.isRequired,
    loadGroups: PropTypes.func.isRequired,
    startAddUser: PropTypes.func.isRequired,
    startViewUser: PropTypes.func.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    submit_func: PropTypes.func,
    addUserRequest: PropTypes.func.isRequired,
    toggleSubUser: PropTypes.func,
    handleToggleDeactivate: PropTypes.func,
    handleCreateUserFormOpen: PropTypes.func,
    handleEditUserFormOpen: PropTypes.func,
    clearForm: PropTypes.func,
    closeModal: PropTypes.func,
  };

  state = {
    searchableUsers: [],
    filteredUsers: [...this.props.users],
    query: undefined,
  };

  componentDidMount() {
    this.props.loadUsers(this.props.user);
    this.props.loadGroups(this.props.user, "userAdmin");
    this.setInitialUsers(this.props.users);
  }

  componentDidUpdate({ users, success }) {
    if (users !== this.props.users) {
      this.setInitialUsers(this.props.users);
    }
    if (!success && this.props.success) {
      this.handleModalClose();
    }
  }

  setInitialUsers = (users) => {
    this.setState({
      searchableUsers: makeSearchable(users),
      filteredUsers: this.props.users,
    });
  };

  handleModalClose = () => {
    const activeModalName = Object.keys(this.props.activeModal)[0];
    this.props.clearForm();
    this.props.closeModal(activeModalName);
  };

  handleSearch = ({ filtered, query }) => {
    this.setState({
      filteredUsers: filtered,
      query,
    });
  };

  render() {
    const {
      user,
      users,
      groups,
      statuses,
      roles,
      handleChange,
      handleSubmit,
      submit_func: submitFunc,
      toggleSubUser,
      handleToggleDeactivate,
      handleCreateUserFormOpen,
      handleEditUserFormOpen,
      addUserRequest,
      modal_data: modalData,
      user_form_data: userFormData,
      editing,
      toggleDeactivateWorking,
      error,
      success,
    } = this.props;

    // If no access, redirect to 404
    if (user.access && !user.access.includes("userAdmin"))
      return <Redirect to="/404" />;

    // This is for the parent user lookup
    const usersLookup = users
      ? users
        .filter(isParentUser)
        .map((u) => ({ value: u.user_id, label: u.user_fullname }))
      : [];

    return (
      <StyledWrap>
        <StyledTitle>Manage Users</StyledTitle>
        <StyledThirds>
          <Left>
            <Button additive thin onClick={handleCreateUserFormOpen}>
              Add User
            </Button>
          </Left>
          <Center>
            <Search
              sessionKey={SEARCH_KEY}
              initialSearch={this.state.query}
              data={this.state.searchableUsers}
              onSearch={this.handleSearch}
              defaultField="targetValueString"
              searchFields={["user_username", "user_fullname", "user_email"]}
              indexId="user_id"
              placeholder="Search Users"
              thick
              cy="Users-Search"
            />
          </Center>
          <Right>
            <Helmet title="Administration" />
          </Right>
        </StyledThirds>
        <MsgAlert type="success" message={success} />

        <CreateUserModal
          title={modalData.title}
          submitText={modalData.submitText}
          handleClose={this.handleModalClose}
          userFormData={userFormData}
          usersLookup={usersLookup}
          handleChange={handleChange}
          addUserRequest={addUserRequest}
          error={error}
          user={user}
          groups={groups}
          rolesSelectOptions={getRolesSelectOptions(user, roles)}
        />

        <EditUserModal
          title={modalData.title}
          submitText={modalData.submitText}
          handleClose={this.handleModalClose}
          userFormData={userFormData}
          usersLookup={usersLookup}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          submitFunc={submitFunc}
          error={error}
          disabled={!editing}
          user={user}
          groups={groups}
          rolesSelectOptions={getRolesSelectOptions(user, roles)}
          handleToggleDeactivate={handleToggleDeactivate}
          toggleDeactivateWorking={toggleDeactivateWorking}
          toggleSubUser={toggleSubUser}
        />

        <Frame>
          <FrameContent>
            {users.length > 0 && (
              <UserTable
                users={this.state.filteredUsers}
                groups={groups}
                onRowSelect={handleEditUserFormOpen}
                statuses={statuses}
                user={user}
              />
            )}
          </FrameContent>
        </Frame>
      </StyledWrap>
    );
  }
}

export default UserAdmin;
